import { safeReturnTo } from '@/lib/return-to';

/**
 * The review page's filters live in the URL (typed search params), so a
 * filtered view can be linked, survives a reload, and Back/Forward walk
 * through it. Every param has a default, so a hand-edited or stale URL can
 * never break the page — anything unrecognised falls back to the default.
 *
 * A plain validator rather than zod on purpose: route `validateSearch`
 * functions ship in the entry chunk, which the sign-in page's budget pays for.
 * So the list itself (filters, sort, whose turn, counts) is in `queue-view.ts`:
 * only the address's parser stays here.
 */
export interface ReviewSearch {
  /** Waiting on me, or already decided. */
  show: 'pending' | 'decided';
  /** Creator fit band (creators and prices only). */
  fit: 'all' | 'strong' | 'good' | 'other';
  /** One campaign (playbook id). */
  playbook?: number;
  /** A saved list the operator pushed creators under. */
  list?: string;
  /** Free-text search on name, handle and list. */
  q?: string;
  sort: 'fit' | 'newest' | 'oldest';
  /** The open item (`influencer:42`). */
  id?: string;
  /** Where the header's back link returns (a same-app path, e.g. the Visibility page she opened it from). */
  back?: string;
  /**
   * The link she left by, for one visit: `steps` — "Two approvals on this campaign."
   * under Approve, whose Campaign details leads back here; `agency` — an agency's name, whose row in Team & roles leads
   * back here. The focus goes back on it, then the address drops it.
   */
  from?: ReviewReturnFrom;
  /**
   * Back from Settings (company rules round 4, F2): the row she had open on Review Content when she followed a pointer
   * to Settings → Draper Knowledge Base, and the list's tab (`RETURN_TABS`). Asked once, then taken out of the address
   * (`use-return-point.ts`).
   */
  point?: string;
  tab?: ReturnTab;
  /**
   * The team the link is about (an email's or a bell's link to one contract: "Your turn to sign"). When it isn't the
   * team she is in and she has a seat on it, the page switches to it first, so the contract opens as linked.
   */
  team?: number;
}

/** The links Review Content takes the focus back to on her way back. */
export const RETURN_FROM = ['steps', 'agency'] as const;
export type ReviewReturnFrom = (typeof RETURN_FROM)[number];

/** Review Content's list tabs, as a way back names them (checklist-lists.ts `ChecklistTab`, written out for the entry chunk). */
export const RETURN_TABS = ['needs', 'all', 'achieved'] as const;
export type ReturnTab = (typeof RETURN_TABS)[number];
const isReturnTab = (value: unknown): value is ReturnTab => RETURN_TABS.some((t) => t === value);

const oneOf = <T extends string>(value: unknown, options: readonly T[], fallback: T): T =>
  typeof value === 'string' && (options as readonly string[]).includes(value) ? (value as T) : fallback;
const text = (value: unknown, max = 200): string | undefined =>
  typeof value === 'string' && value.length > 0 && value.length <= max ? value : undefined;
const positiveInt = (value: unknown): number | undefined => {
  const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : Number.NaN;
  return Number.isInteger(n) && n > 0 ? n : undefined;
};

/** What a link may pass: every param is optional (the parser fills defaults). */
export type ReviewSearchInput = Partial<ReviewSearch>;

export function parseReviewSearch(input: ReviewSearchInput | Record<string, unknown>): ReviewSearch {
  const raw = input as Record<string, unknown>;
  const out: ReviewSearch = {
    show: oneOf(raw.show, ['pending', 'decided'] as const, 'pending'),
    fit: oneOf(raw.fit, ['all', 'strong', 'good', 'other'] as const, 'all'),
    sort: oneOf(raw.sort, ['fit', 'newest', 'oldest'] as const, 'fit'),
  };
  const playbook = positiveInt(raw.playbook);
  if (playbook) out.playbook = playbook;
  const list = text(raw.list);
  if (list) out.list = list;
  const q = text(raw.q);
  if (q) out.q = q;
  const id = text(raw.id, 100);
  if (id) out.id = id;
  const back = text(raw.back, 200);
  if (back && safeReturnTo(back, '') === back) out.back = back;
  const from = oneOf(raw.from, [...RETURN_FROM, ''] as const, '');
  if (from) out.from = from;
  const point = text(raw.point, 100);
  if (point) out.point = point;
  if (isReturnTab(raw.tab)) out.tab = raw.tab;
  const team = positiveInt(raw.team);
  if (team) out.team = team;
  return out;
}

/**
 * The header's way back: to Operations, Road or Deals, where she opened the review (`href` keeps its campaign and where
 * she was; an address from before Operations, `/visibility…`, still counts, and lands on Road); to Artifacts, with its
 * tab, campaign and search (Kazzy, 12 Oct 2026); else Home.
 */
export function reviewBackLink(search: Pick<ReviewSearch, 'back'>): { label: 'Operations' | 'Artifacts' | 'Home'; to: '/operations' | '/artifacts' | '/'; href: string } {
  const back = search.back;
  if (back && /^\/(?:operations|visibility)(?:[/?#]|$)/.test(back)) return { label: 'Operations', to: '/operations', href: back };
  if (back && /^\/artifacts(?:[/?#]|$)/.test(back)) return { label: 'Artifacts', to: '/artifacts', href: back };
  return { label: 'Home', to: '/', href: '/' };
}
